import { useState } from 'react'
import DataTable from '../../components/DataTable'
import Modal from '../../components/Modal'
import StatsCard from '../../components/StatsCard'
import { mockSupportTickets } from '../../data/mockData'
import { formatDate, formatDistanceDate } from '../../utils/formatters'
import { MessageSquare, Clock, AlertCircle, CheckCircle, Send } from 'lucide-react'

export default function SupportPage() {
  const [sortKey, setSortKey] = useState('created')
  const [sortOrder, setSortOrder] = useState('desc')
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState('All')
  const [filterPriority, setFilterPriority] = useState('All')
  const [replyText, setReplyText] = useState('')

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
  }

  const handleRowClick = (ticket) => {
    setSelectedTicket(ticket)
    setShowDetailModal(true)
  }

  const handleReply = () => {
    if (replyText.trim()) {
      alert('Reply sent to ' + selectedTicket.user)
      setReplyText('')
    }
  }

  // Filter tickets
  let filteredTickets = mockSupportTickets
  if (filterStatus !== 'All') {
    filteredTickets = filteredTickets.filter((t) => t.status === filterStatus)
  }
  if (filterPriority !== 'All') {
    filteredTickets = filteredTickets.filter((t) => t.priority === filterPriority)
  }

  // Calculate metrics
  const openCount = mockSupportTickets.filter((t) => t.status === 'Open').length
  const inProgressCount = mockSupportTickets.filter((t) => t.status === 'In Progress').length
  const closedCount = mockSupportTickets.filter((t) => t.status === 'Closed').length
  const highPriorityCount = mockSupportTickets.filter((t) => t.priority === 'High').length

  const columns = [
    { key: 'id', label: 'Ticket ID', sortable: true },
    { key: 'title', label: 'Title', sortable: true },
    { key: 'user', label: 'User', sortable: true },
    { key: 'category', label: 'Category', sortable: false },
    {
      key: 'priority',
      label: 'Priority',
      render: (value) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          value === 'High' ? 'bg-red-100 text-red-700' :
          value === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
          'bg-green-100 text-green-700'
        }`}>{value}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          value === 'Open' ? 'bg-blue-100 text-blue-700' :
          value === 'In Progress' ? 'bg-yellow-100 text-yellow-700' :
          'bg-green-100 text-green-700'
        }`}>{value}</span>
      ),
    },
    { key: 'created', label: 'Created', render: (v) => formatDistanceDate(v) },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-pawssible-slate-900">Support Tickets</h1>
        <p className="text-pawssible-slate-600 mt-1">Customer support & ticket management</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          label="Open Tickets"
          value={openCount}
          icon={AlertCircle}
          color="teal"
        />
        <StatsCard
          label="In Progress"
          value={inProgressCount}
          icon={Clock}
          color="amber"
        />
        <StatsCard
          label="Resolved"
          value={closedCount}
          icon={CheckCircle}
          color="teal"
        />
        <StatsCard
          label="High Priority"
          value={highPriorityCount}
          icon={MessageSquare}
          color="amber"
        />
      </div>

      {/* Filter Controls */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Filter by Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input"
            >
              <option>All</option>
              <option>Open</option>
              <option>In Progress</option>
              <option>Closed</option>
            </select>
          </div>
          <div>
            <label className="label">Filter by Priority</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="input"
            >
              <option>All</option>
              <option>High</option>
              <option>Medium</option>
              <option>Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tickets Table */}
      <div>
        <h2 className="text-xl font-semibold text-pawssible-slate-900 mb-4">All Tickets</h2>
        <DataTable
          columns={columns}
          data={filteredTickets}
          onSort={handleSort}
          sortKey={sortKey}
          sortOrder={sortOrder}
          pageSize={15}
          onRowClick={handleRowClick}
        />
      </div>

      {/* Ticket Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title={selectedTicket ? `Ticket ${selectedTicket.id}: ${selectedTicket.title}` : ''}
        size="lg"
        actions={
          <>
            <button
              onClick={() => setShowDetailModal(false)}
              className="btn-secondary"
            >
              Close
            </button>
            <select className="input" defaultValue={selectedTicket?.status || ''}>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Closed">Closed</option>
            </select>
          </>
        }
      >
        {selectedTicket && (
          <div className="space-y-6">
            {/* Ticket Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-pawssible-slate-600 uppercase tracking-wider">User</p>
                <p className="text-lg font-semibold text-pawssible-slate-900 mt-1">{selectedTicket.user}</p>
              </div>
              <div>
                <p className="text-xs text-pawssible-slate-600 uppercase tracking-wider">Category</p>
                <p className="text-lg font-semibold text-pawssible-slate-900 mt-1">{selectedTicket.category}</p>
              </div>
              <div>
                <p className="text-xs text-pawssible-slate-600 uppercase tracking-wider">Priority</p>
                <span className={`inline-block px-2 py-1 rounded text-xs font-medium mt-1 ${
                  selectedTicket.priority === 'High' ? 'bg-red-100 text-red-700' :
                  selectedTicket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {selectedTicket.priority}
                </span>
              </div>
              <div>
                <p className="text-xs text-pawssible-slate-600 uppercase tracking-wider">Status</p>
                <span className={`inline-block px-2 py-1 rounded text-xs font-medium mt-1 ${
                  selectedTicket.status === 'Open' ? 'bg-blue-100 text-blue-700' :
                  selectedTicket.status === 'In Progress' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {selectedTicket.status}
                </span>
              </div>
            </div>

            {/* Description */}
            <div>
              <p className="text-xs text-pawssible-slate-600 uppercase tracking-wider font-semibold">Description</p>
              <p className="text-pawssible-slate-700 mt-2">{selectedTicket.description}</p>
            </div>

            {/* Reply Section */}
            <div className="border-t border-pawssible-slate-200 pt-4">
              <p className="text-xs text-pawssible-slate-600 uppercase tracking-wider font-semibold">Send Reply</p>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your response..."
                className="input mt-2 min-h-24 resize-none"
              />
              <button
                onClick={handleReply}
                className="btn-primary mt-2 flex items-center gap-2"
              >
                <Send size={16} />
                Send Reply
              </button>
            </div>

            {/* Metadata */}
            <div className="text-sm text-pawssible-slate-500">
              <p>Created: {formatDate(selectedTicket.created)}</p>
              <p>Ticket ID: {selectedTicket.id}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
